import React, { FC, useMemo } from 'react';

import { useFormikContext } from 'formik';

import BasicButton from 'components/buttons/basicButton';
import { SearchableSelectType } from 'components/inputs/searchableSelect';
import AampeCreateSelectDropdown from 'components/shared/AampeCreateSelectDropdown/AampeCreateSelectDropdown';
import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';

import useGrantEntityPermission from 'hooks/entity-permissions/useGrantEntityPermission';
import useTeamMemberQuery from 'hooks/team/useTeamMembersQuery';

import { EntityType } from 'models/entity-permission.model';
import { UserRole } from 'models/user.model';

import { EntityPermissionUserFormikContext } from './constant';

interface EditorSelectionContainerProps {
    formulaId: number;
    hasWriteAccess: boolean;
}
const EditorSelectionContainer: FC<EditorSelectionContainerProps> = ({
    formulaId,
    hasWriteAccess,
}) => {
    const { values, setFieldValue, resetForm } =
        useFormikContext<EntityPermissionUserFormikContext>();
    const { data, isLoading } = useTeamMemberQuery(UserRole.EDITOR);
    const grantPermissionMutation = useGrantEntityPermission(
        EntityType.formula,
        formulaId.toString(),
    );
    const alreadySelectedEditorIds = useMemo<number[]>(() => {
        return values.groups.editors.map((editor) => editor.id);
    }, [values]);

    const options = useMemo<SearchableSelectType[]>(() => {
        if (data) {
            return data
                .filter((users) => !alreadySelectedEditorIds.includes(users.id))
                .map((user) => ({
                    label: user.username,
                    value: user.id,
                }));
        }
        return [];
    }, [data, alreadySelectedEditorIds]);

    const grantPermissionHandler = () => {
        grantPermissionMutation.mutate(
            {
                entity_id: formulaId.toString(),
                entity_type: EntityType.formula,
                user_ids:
                    (values.selectedEditors.map(
                        (editor) => editor.value,
                    ) as number[]) || [],
            },
            {
                onSuccess: () => {
                    resetForm();
                },
            },
        );
    };

    if (!hasWriteAccess) {
        return "You don't have permission to edit this message. Please reach out to your admin or manager to request access.";
    }

    return (
        <>
            <Container>
                <AampeCreateSelectDropdown
                    label={''}
                    wrapperStyle={{ minWidth: '76%' }}
                    creatable={false}
                    options={options}
                    isMultiple
                    menuPortalTarget={document.body}
                    name={'selectedEditors'}
                    value={values.selectedEditors}
                    onChange={(field, value) => setFieldValue(field, value)}
                    placeholder={'Select an Editor'}
                    isLoading={isLoading}
                />
                <BasicButton
                    name="Share"
                    isCompact
                    isDisabled={!values.selectedEditors.length}
                    color={aampeTheme.colors.solidA01}
                    onClick={grantPermissionHandler}
                />
            </Container>
        </>
    );
};

const Container = styled.div({
    display: 'flex',
    width: '100%',
    gap: aampeTheme.spacing4(3),
});

export default EditorSelectionContainer;
